// src/pages/auth/pages/ForgotPassword.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// ICONS
import { ReactComponent as MailIcon } from "../../../../assets/icons/sms.svg";
import { ReactComponent as LockIcon } from "../../../../assets/icons/lock.svg";

// UI
import InputField from "../../components/InputFeild";

// API
import { userService } from "../../../../services/api/authenService";
import { AuthContext } from "../../../../contexts/AuthContext";

// CONTEXT

// ====== Config ======
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const ForgotPassword = () => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  // ======= Step & loading =======
  // 1: Nhập email, 2: OTP, 3: Mật khẩu mới
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ======= Step 1 =======
  const [email, setEmail] = useState("");

  // ======= Step 2 (OTP) =======
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const otpRefs = useRef(
    Array.from({ length: OTP_LENGTH }, () => React.createRef())
  );
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [otpShakeKey, setOtpShakeKey] = useState(0); // trigger shake khi sai

  // ======= Step 3 (reset) =======
  const [token, setToken] = useState(""); // token xác thực từ verify_code
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ======= Misc =======
  const [focusField, setFocusField] = useState(null);
  const [errors, setErrors] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  // ======= Validate =======
  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const validatePassword = (v) => (v ?? "").length >= 6;

  const otpValue = useMemo(() => otp.join(""), [otp]);

  // ======= OTP countdown theo step 2 =======
  useEffect(() => {
    if (step !== 2) return;
    setTimer(RESEND_SECONDS);
    setCanResend(false);

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  // ======= Helpers =======
  const iconColor = (field) => (focusField === field ? "#1D4ED8" : "#9E9E9E");

  const focusOtpIndex = (idx) => {
    const ref = otpRefs.current[idx];
    ref?.current?.focus?.();
    ref?.current?.select?.();
  };

  const handleOtpChange = (index, char) => {
    const value = (char ?? "").replace(/\D/g, ""); // chỉ số
    setOtp((prev) => {
      const n = [...prev];
      n[index] = value.slice(-1) ?? "";
      return n;
    });
    if (value && index < OTP_LENGTH - 1) {
      focusOtpIndex(index + 1);
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setOtp((prev) => {
        const n = [...prev];
        if (n[index]) {
          n[index] = "";
          return n;
        }
        if (index > 0) {
          n[index - 1] = "";
          setTimeout(() => focusOtpIndex(index - 1), 0);
        }
        return n;
      });
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusOtpIndex(index - 1);
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      e.preventDefault();
      focusOtpIndex(index + 1);
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const chars = pasted.slice(0, OTP_LENGTH).split("");
    const filled = Array(OTP_LENGTH)
      .fill("")
      .map((_, i) => chars[i] ?? "");
    setOtp(filled);
    const lastIndex = Math.min(chars.length - 1, OTP_LENGTH - 1);
    if (lastIndex >= 0) focusOtpIndex(lastIndex);
  };

  // ======= Submit: Step 1 - gửi OTP =======
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setErrors((s) => ({ ...s, email: "Email không hợp lệ." }));
      return;
    }
    setErrors({ email: "", newPassword: "", confirmPassword: "" });

    try {
      setLoading(true);
      await userService.forgot_password(email);
      toast.success("Mã OTP đã được gửi tới email.");
      setStep(2); // chỉ nhảy step khi API OK
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeout(() => focusOtpIndex(0), 0);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message || "Không gửi được OTP. Thử lại sau!"
      );
    } finally {
      setLoading(false);
    }
  };

  // ======= Resend OTP =======
  const handleResendOTP = async () => {
    if (!canResend || !validateEmail(email)) return;
    try {
      setLoading(true);
      await userService.forgot_password(email);
      toast.success("Đã gửi lại OTP.");
      // chỉ reset timer khi API OK
      setTimer(RESEND_SECONDS);
      setCanResend(false);
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeout(() => focusOtpIndex(0), 0);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Không gửi lại được OTP!");
    } finally {
      setLoading(false);
    }
  };

  // ======= Submit: Step 2 - verify OTP =======
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const otpStr = String(otpValue).replace(/\D/g, "");
    if (otpStr.length !== OTP_LENGTH) {
      toast.info(`Vui lòng nhập đủ ${OTP_LENGTH} số OTP.`);
      return;
    }

    try {
      setLoading(true);
      const res = await userService.verify_code(email, otpStr);
      if (!res?.token)
        throw new Error("Thiếu token xác thực đặt lại mật khẩu.");
      setToken(res.token);

      // Hiệu ứng “success flash” nhẹ cho dãy OTP
      toast.success("Xác thực OTP thành công.");
      setStep(3);
      setTimeout(() => {
        const el = document.querySelector('input[name="newPassword"]');
        el?.focus?.();
      }, 0);
    } catch (error) {
      // Trigger shake dãy OTP
      setOtpShakeKey((k) => k + 1);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Mã OTP không hợp lệ!");
    } finally {
      setLoading(false);
    }
  };

  // ======= Submit: Step 3 - reset password (+ auto-login) =======
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validatePassword(newPassword)) {
      setErrors((s) => ({
        ...s,
        newPassword: "Mật khẩu phải có ít nhất 6 ký tự.",
      }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrors((s) => ({
        ...s,
        confirmPassword: "Mật khẩu xác nhận không khớp!",
      }));
      return;
    }
    setErrors({ email: "", newPassword: "", confirmPassword: "" });

    try {
      setLoading(true);
      const userData = await userService.reset_password(token, newPassword);
      toast.success("Đặt lại mật khẩu thành công!");

      // Auto-login (nếu BE cho phép)
      const loginRes = await userService.log_in(
        userData?.account?.identifier,
        newPassword
      );

      // Chuẩn hoá payload về { token, refreshToken, user }
      if (loginRes?.token || loginRes?.accessToken) {
        dispatch({
          type: "LOGIN",
          payload: {
            token_client: loginRes.token,
            user_client: loginRes.account,
          },
        });
        navigate("/");
      } else {
        navigate("/login");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message || "Không thể đặt lại mật khẩu."
      );
    } finally {
      setLoading(false);
    }
  };

  // ======= UI computed =======
  const canSubmitEmail = validateEmail(email) && !loading;
  const canSubmitCode = otpValue.length === OTP_LENGTH && !loading;
  const canSubmitReset =
    validatePassword(newPassword) &&
    newPassword === confirmPassword &&
    !loading;

  // phần trăm tiến độ resend (0 → 100)
  const resendPercent = useMemo(
    () => Math.round(((RESEND_SECONDS - timer) / RESEND_SECONDS) * 100),
    [timer]
  );

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      {/* Stepper */}
      <div className="mb-4 flex w-full  max-w-md  flex-col items-center">
        <div className="relative  flex w-full items-center justify-evenly">
          {/* Step 1 */}
          <div className="z-[100] flex flex-col items-center justify-center">
            <div
              className={`size-10 flex items-center justify-center rounded-full border text-sm text-white transition-transform duration-200 ${
                step >= 1
                  ? "bg-blue-500 scale-110 shadow-lg shadow-blue-500/30"
                  : "bg-gray-400"
              }`}
              style={{ zIndex: 20 }}
            >
              1
            </div>
            <span
              className={`mt-1 whitespace-nowrap text-sm transition-colors ${
                step >= 1 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              Nhập Email
            </span>
          </div>

          <div
            className={`absolute left-[70px] top-5 z-10 h-1 w-1/3 overflow-hidden rounded bg-gray-300`}
          >
            <div
              className={`h-full bg-blue-500 transition-all duration-500`}
              style={{ width: step >= 2 ? "100%" : "0%" }}
            />
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center justify-center">
            <div
              className={`size-10 flex items-center justify-center rounded-full border text-sm text-white transition-transform duration-200 ${
                step >= 2
                  ? "bg-blue-500 scale-110 shadow-lg shadow-blue-500/30"
                  : "bg-gray-400"
              }`}
              style={{ zIndex: 20 }}
            >
              2
            </div>
            <span
              className={`mt-1 whitespace-nowrap text-sm transition-colors ${
                step >= 2 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              Xác thực OTP
            </span>
          </div>

          <div className="absolute right-20 top-5 h-1 w-1/3 overflow-hidden rounded bg-gray-300">
            <div
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: step >= 3 ? "100%" : "0%" }}
            />
          </div>

          {/* Step 3 */}
          <div className="flex flex-col items-center justify-center">
            <div
              className={`size-10 flex items-center justify-center rounded-full border text-sm text-white transition-transform duration-200 ${
                step >= 3
                  ? "bg-blue-500 scale-110 shadow-lg shadow-blue-500/30"
                  : "bg-gray-400"
              }`}
              style={{ zIndex: 20 }}
            >
              3
            </div>
            <span
              className={`mt-1 whitespace-nowrap text-sm transition-colors ${
                step >= 3 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              Đổi mật khẩu
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex w-full flex-col items-center justify-between">
        <h1
          className="mt-2 mb-3 text-2xl font-bold opacity-0 animate-fade-in-up"
          style={{ animationDelay: "80ms" }}
        >
          {step === 1 && "Quên mật khẩu"}
          {step === 2 && "Xác thực mã OTP"}
          {step === 3 && "Đặt lại mật khẩu mới"}
        </h1>

        {/* Step 1: Email */}
        {step === 1 && (
          <form
            onSubmit={handleEmailSubmit}
            className="w-full  space-y-6 animate-slide-in"
          >
            <InputField
              label="Email"
              type="email"
              name="email"
              placeholder="Nhập email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              IconComponent={MailIcon}
              error={errors.email}
              iconColor={iconColor("email")}
              onFocus={() => setFocusField("email")}
              onBlur={() => setFocusField(null)}
              autoComplete="email"
              autoFocus
            />

            <button
              type="submit"
              className={`group mt-3 w-full rounded py-2 text-white transition-all ${
                canSubmitEmail
                  ? "bg-blue-500 hover:bg-blue-600 active:scale-[0.99]"
                  : "bg-blue-300 cursor-default"
              }`}
              disabled={!canSubmitEmail}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading && <span className="spinner" aria-hidden />}
                {loading ? "Đang gửi..." : "Gửi mã OTP"}
              </span>
            </button>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <form
            onSubmit={handleCodeSubmit}
            className="flex w-full  flex-col items-end animate-slide-in"
          >
            <div
              key={otpShakeKey}
              className="mb-3 flex w-full items-center justify-evenly gap-2 animate-scale-in"
            >
              {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                <input
                  key={index}
                  ref={otpRefs.current[index]}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  className="h-16 w-16 rounded border text-center text-xl font-bold outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-300/60 otp-cell"
                  value={otp[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  aria-label={`Mã OTP ký tự thứ ${index + 1}`}
                />
              ))}
            </div>

            <button
              type="submit"
              className={`group mt-1 w-full rounded py-2 text-white transition-all ${
                canSubmitCode
                  ? "bg-blue-500 hover:bg-blue-600 active:scale-[0.99]"
                  : "bg-blue-300 cursor-default"
              }`}
              disabled={!canSubmitCode}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading && <span className="spinner" aria-hidden />}
                {loading ? "Đang xử lý..." : "Xác nhận"}
              </span>
            </button>

            {/* Resend OTP + Progress bar */}
            <div className="mt-3 w-full">
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={!canResend || loading}
                  className={`${
                    canResend && !loading
                      ? "text-blue-600 hover:underline"
                      : "text-gray-400 cursor-default"
                  }`}
                >
                  {canResend ? "Gửi lại OTP" : `Gửi lại sau ${timer}s`}
                </button>
                {!canResend && (
                  <span className="text-gray-500">{resendPercent}%</span>
                )}
              </div>
              {!canResend && (
                <div className="mt-1 h-1 w-full overflow-hidden rounded bg-gray-200">
                  <div
                    className="h-full bg-blue-500 transition-[width] duration-1000 ease-linear"
                    style={{ width: `${resendPercent}%` }}
                  />
                </div>
              )}
            </div>
          </form>
        )}

        {/* Step 3: Reset password */}
        {step === 3 && (
          <form
            onSubmit={handleResetPassword}
            className="w-full  space-y-6 animate-slide-in"
          >
            <InputField
              label="Mật khẩu mới"
              type={showPassword ? "text" : "password"}
              name="newPassword"
              placeholder="Nhập mật khẩu mới"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              IconComponent={LockIcon}
              toggleVisibility={() => setShowPassword((s) => !s)}
              showPassword={showPassword}
              error={errors.newPassword}
              iconColor={iconColor("newPassword")}
              onFocus={() => setFocusField("newPassword")}
              onBlur={() => setFocusField(null)}
              autoComplete="new-password"
            />
            <InputField
              label="Xác nhận mật khẩu"
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Xác nhận mật khẩu"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              IconComponent={LockIcon}
              toggleVisibility={() => setShowPassword((s) => !s)}
              showPassword={showPassword}
              error={errors.confirmPassword}
              iconColor={iconColor("confirmPassword")}
              onFocus={() => setFocusField("confirmPassword")}
              onBlur={() => setFocusField(null)}
              autoComplete="new-password"
            />
            <button
              type="submit"
              className={`group mt-1 w-full rounded py-2 text-white transition-all ${
                canSubmitReset
                  ? "bg-green-500 hover:bg-green-600 active:scale-[0.99]"
                  : "bg-green-300 cursor-default"
              }`}
              disabled={!canSubmitReset}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading && (
                  <span className="spinner spinner--light" aria-hidden />
                )}
                {loading ? "Đang xử lý..." : "Đặt lại mật khẩu"}
              </span>
            </button>
          </form>
        )}

        {/* Back to Login */}
        <div
          className="mt-4 text-sm opacity-0 animate-fade-in"
          style={{ animationDelay: "120ms", animationFillMode: "forwards" }}
        >
          Quay lại?{" "}
          <Link
            to="/login"
            className="font-sf-medium text-blue-600 hover:underline"
          >
            Đăng nhập
          </Link>
        </div>
      </div>

      {/* Local CSS cho animation nhỏ gọn, không cần thêm lib */}
      <style>{`
        .spinner {
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          border: 2px solid rgba(255,255,255,0.6);
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }
        .spinner--light {
          border: 2px solid rgba(255,255,255,0.6);
          border-top-color: #fff;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .animate-fade-in-up {
          animation: fadeInUp 380ms cubic-bezier(0.22,1,0.36,1) forwards;
        }
        .animate-fade-in {
          animation: fadeIn 420ms ease forwards;
        }
        .animate-slide-in {
          animation: slideIn 420ms cubic-bezier(0.22,1,0.36,1) forwards;
        }
        .animate-scale-in {
          animation: scaleIn 280ms cubic-bezier(0.2,0,0,1) forwards;
        }
        .otp-cell {
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
        }
        .otp-cell:focus {
          box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.12);
          transform: translateZ(0) scale(1.02);
        }

        /* Khi cần “shake” dãy OTP (trigger bằng key change) */
        [key]:has(.otp-cell) { /* no-op, giữ để dễ tìm group nếu cần */ }
        .animate-shake {
          animation: shakeX 380ms cubic-bezier(.36,.07,.19,.97) both;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes shakeX {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
